// ClientEngagements.jsx

// Dependencies
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTable } from 'react-table';

// Components
import EngagementNotes from '@/components/data/engagements/EngagementNotes';

// Styles
import { professionalengagementsCss } from '@digital_monorepo/styling';

const ClientEngagements = ({ engagements }) => {
  const { t } = useTranslation();

  const columns = useMemo(() => [
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.ENGAGEMENT_ID'),
      accessor: 'EngagementID',
      sticky: 'left',
      width: 150,
      Cell: ({ value }) => <Link to={`/engagements/${value}`} className={professionalengagementsCss.linkCell}>{value}</Link>,
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.ENGAGEMENT_NAME'),
      accessor: 'EngagementName',
      sticky: 'left',
      width: 200,
      Cell: ({ value }) => (
        <div className={professionalengagementsCss.engagementNameCell} title={value}>
          {value}
        </div>
      ),
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.TYPE'),
      accessor: 'Type',
      width: 100,
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.STATUS'),
      accessor: 'Status',
      width: 120,
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.MANAGER'),
      accessor: 'Manager',
      width: 150,
      Cell: ({ value }) => value ? (
        <Link to={`/professionals/${value}`} className={professionalengagementsCss.linkCell}>
          <div className={professionalengagementsCss.truncatedCell} title={value}>
            {value}
          </div>
        </Link>
      ) : '',
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.ENGAGEMENT_NOTES'),
      accessor: 'EngagementNotes',
      width: 200,
      Cell: ({ row }) => (
        <EngagementNotes
          engagementID={row.original.EngagementID}
          engagementNotes={row.original.EngagementNotes}
        />
      ),
    },
  ], [t]);

  const data = useMemo(() => engagements, [engagements]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data,
  });

  return (
    <div className={professionalengagementsCss.sectionContainer}>
      <div className={professionalengagementsCss.sectionHeader}>
        <h3 className={professionalengagementsCss.sectionTitle}>
          {t('ENGAGEMENTS_TABLE.TITLE')}
        </h3>
      </div>
      <div className={professionalengagementsCss.tableWrapper}>
        <div className={professionalengagementsCss.tableContainer}>
          <table {...getTableProps()} className={professionalengagementsCss.table}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} {...restHeaderGroupProps} className={professionalengagementsCss.headerRow}>
                  {headerGroup.headers.map((column) => {
                    const { key, ...restColumn } = column.getHeaderProps();
                    const stickyClass = column.sticky === 'left' ? `${professionalengagementsCss.sticky} ${professionalengagementsCss.headerSticky}` : '';
                    return (
                      <th
                        key={key}
                        {...restColumn}
                        className={`${professionalengagementsCss.th} ${stickyClass}`}
                        style={{ width: column.width }}
                      >
                        {column.render('Header')}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const { key, ...restRowProps } = row.getRowProps();
              return (
                <tr key={key} {...restRowProps} className={professionalengagementsCss.bodyRow}>
                  {row.cells.map((cell) => {
                    const { key, ...restCellProps } = cell.getCellProps();
                    const stickyClass = cell.column.sticky === 'left' ? `${professionalengagementsCss.sticky} ${professionalengagementsCss.bodySticky}` : '';
                    const isNotesColumn = cell.column.id === 'EngagementNotes';
                    const cellStyle = isNotesColumn 
                      ? { width: cell.column.width, justifyContent: 'flex-start' }
                      : { width: cell.column.width };
                    return (
                      <td
                        key={key}
                        {...restCellProps}
                        className={`${professionalengagementsCss.td} ${stickyClass}`}
                        style={cellStyle}
                      >
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
};

export default ClientEngagements;
